import { useEffect } from 'react'
import { router } from '@inertiajs/react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'

import { type AlertType, useAlertsStore } from '@stores/alertsStore'
import { FormDialog } from '@components/molecules/FormDialog'
import { scopedTranslation } from '@frontend/utils/I18n'
import { Checkbox } from '@atoms'

import type { UpdateContactPreferencesDialogState } from '../../types/dialogTypes'

const t = scopedTranslation('components.contact_preferences_dialog')

const ALERTS_PATH = 'updateContactPreferences' as const

const schema = yup
  .object({
    email: yup.boolean().label(t('form.labels.email')),
    sms: yup.boolean().label(t('form.labels.sms')),
    phone: yup.boolean().label(t('form.labels.phone_calls')),
  })
  .required()

type FormData = {
  email?: boolean
  sms?: boolean
  phone?: boolean
}

type ContactPreferencesDialogProps = {
  onClose: () => void
  dialogState: UpdateContactPreferencesDialogState
}

export const ContactPreferencesDialog = ({ onClose, dialogState }: ContactPreferencesDialogProps) => {
  const {
    dialogData,
    dialogData: { supporter, currentContactPreferences },
  } = dialogState
  const { stopReceivingEmails, stopReceivingSms, stopReceivingCalls } = currentContactPreferences
  const completelyUnsubscribedAlready = stopReceivingEmails && stopReceivingSms && stopReceivingCalls
  const supporterId = supporter?.id
  const supporterFullName = `${supporter.firstName ?? ''} ${supporter.lastName ?? ''}`.trim()
  const supporterName = supporter.firstName ? supporter.firstName : supporter.lastName
  const groupId = dialogData.groupId
  const { setAlerts, clearAlerts } = useAlertsStore(({ setAlerts, clearAlerts }) => ({
    setAlerts,
    clearAlerts,
  }))

  const {
    handleSubmit,
    formState: { errors, isLoading, isSubmitting },
    reset,
    control,
    watch,
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    reValidateMode: 'onBlur',
    defaultValues: {
      email: !stopReceivingEmails,
      sms: !stopReceivingSms,
      phone: !stopReceivingCalls,
    },
  })

  const setAlert = (type: AlertType['type'], title: string) => {
    setAlerts({
      alertsPath: ALERTS_PATH,
      alerts: [{ type, details: { title } }],
    })
  }

  const receiveEmails = watch('email')
  const receiveSms = watch('sms')
  const receiveCalls = watch('phone')

  useEffect(() => {
    if (!completelyUnsubscribedAlready && !receiveEmails && !receiveSms && !receiveCalls) {
      setAlert('warning', t('alerts.warning.unsubscribe_confirmation', { first_name: supporterName ?? '' }))
    } else {
      clearAlerts(ALERTS_PATH)
    }
  }, [receiveEmails, receiveSms, receiveCalls])

  const onValid = (data: FormData) => {
    router.post(`/groups/${groupId}/supporters/${supporterId}/update_contact_preferences`, data, {
      preserveState: false,
    })
  }

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      reset()
      clearAlerts(ALERTS_PATH)
      onClose()
    }
  }

  return (
    <FormDialog.Root
      open={true}
      onOpenChange={handleOpenChange}
      testId="contact-preferences-dialog"
      preventOutsideClick
    >
      <FormDialog.Title alertsPath={ALERTS_PATH}>{t('title', { name: supporterFullName })}</FormDialog.Title>
      <FormDialog.Form onSubmit={handleSubmit(onValid)}>
        <FormDialog.Body>
          <fieldset className="tw:pb-5 tw:space-y-5">
            <legend className="tw:text-lg tw:text-inherit tw:mb-1 tw:leading-none tw:tracking-tight">
              {t('form.checkbox_legend', { first_name: supporterName ?? '' })}
            </legend>

            <div className="tw:flex tw:items-center">
              <Checkbox control={control} name="email" defaultChecked={receiveEmails} errors={errors}>
                {t('form.labels.email')}
              </Checkbox>
            </div>
            <div className="tw:flex tw:items-center">
              <Checkbox control={control} name="sms" defaultChecked={receiveSms} errors={errors}>
                {t('form.labels.sms')}
              </Checkbox>
            </div>
            <div className="tw:flex tw:items-center">
              <Checkbox control={control} name="phone" defaultChecked={receiveCalls} errors={errors}>
                {t('form.labels.phone_calls')}
              </Checkbox>
            </div>
          </fieldset>
        </FormDialog.Body>
        <FormDialog.Footer submitText={t('form.submit')} isLoading={isLoading || isSubmitting} />
      </FormDialog.Form>
    </FormDialog.Root>
  )
}
